import { useMemo, ComponentProps } from 'react';
import { Goal, HabitLinkedAction } from '@/types/goal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { HabitCheckbox } from '@/components/HabitCheckbox';
import { CalendarCheck, PartyPopper, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { HabitToggleResult } from '@/components/dashboard/HabitTrackingTab';
import { isHabitDueToday } from '@/lib/habit-schedule';
import { InlineCoachHint } from './InlineCoachHint';
import { ReflectionPrompt } from './ReflectionPrompt';
import type { InlineHint } from '@/hooks/useInlineHints';
import { format } from 'date-fns';

type ReflectionProps = Omit<ComponentProps<typeof ReflectionPrompt>, 'allHabitsComplete' | 'totalHabits'>;

interface TodaysHabitsProps {
  goals: Goal[];
  onToggleHabit: (goalId: string, habitId: string) => Promise<HabitToggleResult | void>;
  onViewAllHabits: () => void;
  onNavigateToPlan?: (milestoneIndex: number) => void;
  coachHints?: Map<string, InlineHint>;
  reflection: ReflectionProps;
}

export const TodaysHabits = ({
  goals,
  onToggleHabit,
  onViewAllHabits,
  onNavigateToPlan,
  coachHints,
  reflection,
}: TodaysHabitsProps) => {
  const today = format(new Date(), 'yyyy-MM-dd');

  const allHabits = useMemo(() => {
    const now = new Date();
    return goals.flatMap(goal =>
      goal.habits
        .filter(habit => isHabitDueToday(habit.frequency, habit.scheduleDays, habit.createdAt, now))
        .map(habit => {
          let isCompletedToday = habit.completedDates.includes(today);
          // Weekly habits: mark as complete if completed any day this week
          if (habit.frequency === 'weekly' && !isCompletedToday) {
            const dayOfWeek = now.getDay();
            const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
            const weekStart = new Date(now);
            weekStart.setDate(weekStart.getDate() + mondayOffset);
            weekStart.setHours(0, 0, 0, 0);
            isCompletedToday = habit.completedDates.some(d => {
              const date = new Date(d + 'T00:00:00');
              return date >= weekStart && date <= now;
            });
          }
          return {
            ...habit,
            goalId: goal.id,
            goalTitle: goal.title,
            isCompletedToday,
            linkedAction: habit.linkedAction,
          };
        })
    );
  }, [goals, today]);

  const completedToday = allHabits.filter(h => h.isCompletedToday).length;
  const totalHabits = allHabits.length;
  const displayedHabits = allHabits.slice(0, 5);
  const hasMoreHabits = totalHabits > 5;
  const progressPercent = totalHabits > 0 ? Math.round((completedToday / totalHabits) * 100) : 0;
  const isAllComplete = totalHabits > 0 && completedToday === totalHabits;
  const remaining = totalHabits - completedToday;

  // Empty state
  if (totalHabits === 0) {
    return (
      <Card className="bg-card/50 border-border/50">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center justify-between text-lg">
            <div className="flex items-center gap-2">
              <CalendarCheck className="w-5 h-5 text-primary" />
              Today's Focus
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center py-8">
          <PartyPopper className="w-12 h-12 mx-auto mb-4 text-primary/60" />
          <p className="text-muted-foreground mb-4">
            No habits scheduled for today. You're all set! 🎉
          </p>
          <Button variant="outline" onClick={onViewAllHabits} className="gap-2">
            View All Habits
            <ArrowRight className="w-4 h-4" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card/50 border-border/50 ring-1 ring-primary/20">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center justify-between text-lg">
          <div className="flex items-center gap-2">
            <CalendarCheck className="w-5 h-5 text-primary" />
            Today's Focus
          </div>
          <span className={cn(
            "text-sm font-medium",
            isAllComplete ? "text-emerald-500" : "text-muted-foreground"
          )}>
            {completedToday}/{totalHabits} complete
            {isAllComplete && " ✓"}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Habits List */}
        <div className="space-y-2">
          {displayedHabits.map(habit => {
            const hint = coachHints?.get(habit.id);
            return (
              <div key={habit.id}>
                <HabitCheckbox
                  title={habit.title}
                  completed={habit.isCompletedToday}
                  streak={habit.streak}
                  onToggle={() => onToggleHabit(habit.goalId, habit.id)}
                  linkedAction={habit.linkedAction}
                  onNavigateToPlan={onNavigateToPlan}
                />
                {hint && <InlineCoachHint message={hint.message} />}
              </div>
            );
          })}
        </div>
        {hasMoreHabits && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onViewAllHabits}
            className="w-full text-muted-foreground hover:text-foreground gap-1"
          >
            Show all {totalHabits} habits
            <ArrowRight className="w-3 h-3" />
          </Button>
        )}

        {/* Progress Bar */}
        <div className="pt-2">
          <Progress 
            value={progressPercent} 
            className={cn(
              "h-2 transition-all duration-500",
              isAllComplete && "[&>div]:bg-emerald-500"
            )}
          />
        </div>

        {/* CTA */}
        {!isAllComplete && remaining > 0 && (
          <p className="text-sm text-muted-foreground pt-2">
            Keep going! <span className="font-medium text-primary">{remaining} habit{remaining !== 1 ? 's' : ''}</span> remaining.
          </p>
        )}

        {isAllComplete && (
          <div className="flex items-center justify-between pt-2">
            <p className="text-sm text-emerald-500 font-medium">
              🎉 All habits complete! Great work!
            </p>
            <Button variant="ghost" size="sm" onClick={onViewAllHabits} className="gap-1 text-muted-foreground">
              View Details
              <ArrowRight className="w-3 h-3" />
            </Button>
          </div>
        )}

        <ReflectionPrompt
          allHabitsComplete={isAllComplete}
          totalHabits={totalHabits}
          {...reflection}
        />
      </CardContent>
    </Card>
  );
};
